import React from 'react';
import { Info, Heart, Calendar, ThermometerSun, Baby, ShieldAlert, AlertTriangle, GripVertical } from 'lucide-react';

/**
 * Компонент с информационным контентом об овуляции и планировании беременности
 */
const InfoContent = ({ formData }) => {
  // Определяем, какой контент показывать в зависимости от цели (планирование беременности или контрацепция)
  const infoTitle = formData.plannedPregnancy 
    ? 'Всё об овуляции и планировании беременности' 
    : 'Всё об овуляции и естественных методах контрацепции';

  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-rose-500" />
        {infoTitle}
      </h2>
      
      <div className="prose prose-rose dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-rose-500" />
          Что такое овуляция?
        </h3>
        <p>
          <strong>Овуляция</strong> — это выход созревшей яйцеклетки из яичника в маточную трубу, где она может быть оплодотворена сперматозоидом. Овуляция обычно происходит примерно в середине менструального цикла, но точное время может варьироваться у разных женщин и даже от цикла к циклу у одной и той же женщины.
        </p>
        <p>
          После выхода из яичника яйцеклетка способна к оплодотворению в течение примерно 24 часов. При этом сперматозоиды могут сохранять жизнеспособность в женском организме до 5-7 дней. Поэтому фертильный период (когда возможно зачатие) длится около 6-7 дней — 5 дней до овуляции и 1-2 дня после неё.
        </p>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-rose-500" />
          Менструальный цикл и его фазы
        </h3>
        <p>
          Менструальный цикл — это периодические изменения в организме женщины, направленные на возможность зачатия и вынашивания ребенка. Отсчитывается с первого дня менструации до первого дня следующей менструации.
        </p>
        <div className="not-prose my-6">
          <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 border-b border-gray-200 dark:border-gray-600">
              <h4 className="text-base font-medium text-gray-900 dark:text-white flex items-center">
                <GripVertical className="w-5 h-5 mr-2 text-rose-500" />
                Фазы менструального цикла
              </h4>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <div className="grid grid-cols-4 divide-x divide-gray-200 dark:divide-gray-700">
                <div className="p-3 bg-rose-50 dark:bg-rose-900/20">
                  <h5 className="font-medium text-rose-700 dark:text-rose-300 text-sm">Фолликулярная фаза</h5>
                  <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                    С 1-го дня цикла до овуляции
                  </p>
                </div>
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20">
                  <h5 className="font-medium text-purple-700 dark:text-purple-300 text-sm">Овуляция</h5>
                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                    Примерно в середине цикла
                  </p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20">
                  <h5 className="font-medium text-blue-700 dark:text-blue-300 text-sm">Лютеиновая фаза</h5>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    12-16 дней после овуляции
                  </p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20">
                  <h5 className="font-medium text-red-700 dark:text-red-300 text-sm">Менструация</h5>
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    3-7 дней в начале цикла
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700">
                <div className="p-3">
                  <h5 className="font-medium text-gray-900 dark:text-white text-sm">Средняя длительность цикла</h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <strong>28 дней</strong>, но нормой считается от 21 до 35 дней
                  </p>
                </div>
                <div className="p-3">
                  <h5 className="font-medium text-gray-900 dark:text-white text-sm">Лютеиновая фаза</h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <strong>14 дней</strong> (± 2 дня) — наиболее стабильная часть цикла
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h3 className="flex items-center gap-2">
          <ThermometerSun className="w-5 h-5 text-rose-500" />
          Признаки и симптомы овуляции
        </h3>
        <p>
          Определить овуляцию можно по ряду физиологических признаков. Их интенсивность индивидуальна — некоторые женщины отмечают все признаки, другие не замечают никаких изменений.
        </p>
        <ul>
          <li>
            <strong>Цервикальная слизь</strong> — становится более прозрачной, эластичной, похожей на яичный белок. Это один из самых надежных природных индикаторов приближающейся овуляции.
          </li>
          <li>
            <strong>Базальная температура</strong> — повышается на 0.2-0.5°C после овуляции и остается повышенной до следующей менструации.
          </li>
          <li>
            <strong>Боль при овуляции (Митте́льшмерц)</strong> — кратковременные боли внизу живота, обычно с одной стороны, в день овуляции.
          </li>
          <li>
            <strong>Повышение либидо</strong> — естественный механизм, способствующий зачатию.
          </li>
          <li>
            <strong>Болезненность груди</strong> — может появляться из-за гормональных изменений.
          </li>
          <li>
            <strong>Мажущие выделения</strong> — незначительные кровянистые выделения у некоторых женщин.
          </li>
          <li>
            <strong>Повышение обоняния</strong> — некоторые женщины отмечают обострение обоняния.
          </li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Методы определения овуляции</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mt-2 space-y-1">
            <li><strong>Тесты на овуляцию</strong> — определяют всплеск лютеинизирующего гормона (ЛГ) за 24-36 часов до овуляции</li>
            <li><strong>Измерение базальной температуры</strong> — график температуры тела, измеренной сразу после пробуждения</li>
            <li><strong>Наблюдение за цервикальной слизью</strong> — изменение консистенции и количества выделений</li>
            <li><strong>УЗИ-мониторинг</strong> — ультразвуковое исследование состояния фолликулов</li>
            <li><strong>Определение прогестерона в крови</strong> — анализ, подтверждающий произошедшую овуляцию</li>
          </ul>
        </div>

        {formData.plannedPregnancy ? (
          <>
            <h3 className="flex items-center gap-2">
              <Baby className="w-5 h-5 text-rose-500" />
              Планирование беременности
            </h3>
            <p>
              Для успешного зачатия важно правильно определить фертильное окно — период, когда вероятность забеременеть максимальна. Наиболее благоприятными для зачатия являются 1-2 дня до овуляции и день овуляции.
            </p>
            <p>
              Помимо отслеживания овуляции, для подготовки к беременности рекомендуется:
            </p>
            <ul>
              <li>Начать принимать фолиевую кислоту (400-800 мкг в день) за 3 месяца до планируемого зачатия</li>
              <li>Отказаться от алкоголя, курения и других вредных привычек</li>
              <li>Придерживаться сбалансированной диеты, богатой витаминами и минералами</li>
              <li>Поддерживать нормальный вес (как избыточный, так и недостаточный вес могут затруднить зачатие)</li>
              <li>Минимизировать стресс и обеспечить полноценный отдых</li>
              <li>Пройти обследование у гинеколога и других специалистов для выявления потенциальных проблем</li>
            </ul>

            <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800">
              <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Когда обратиться к врачу
              </h4>
              <p className="text-yellow-600 dark:text-yellow-400 text-sm mt-2">
                Если беременность не наступает в течение 12 месяцев регулярной половой жизни без контрацепции (6 месяцев для женщин старше 35 лет), рекомендуется обратиться к репродуктологу. Раннее обращение также показано при нерегулярных циклах, эндометриозе, СПКЯ, непроходимости маточных труб или заболеваниях репродуктивной системы у партнера.
              </p>
            </div>
          </>
        ) : (
          <>
            <h3 className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-500" />
              Естественные методы контрацепции
            </h3>
            <p>
              Календарный метод контрацепции основан на воздержании от полового акта в фертильные дни цикла. Этот метод имеет относительно низкую эффективность (около 76-88% при идеальном использовании) и требует высокой дисциплины.
            </p>
            <p>
              Основные естественные методы контрацепции включают:
            </p>
            <ul>
              <li><strong>Календарный метод</strong> — расчет фертильных дней на основе данных о предыдущих циклах</li>
              <li><strong>Метод базальной температуры</strong> — определение момента овуляции по изменению температуры</li>
              <li><strong>Метод цервикальной слизи (метод Биллингса)</strong> — наблюдение за изменениями цервикальной слизи</li>
              <li><strong>Симптотермальный метод</strong> — комбинирование наблюдений за температурой и цервикальной слизью</li>
            </ul>

            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800">
              <h4 className="text-red-700 dark:text-red-300 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Важное предупреждение
              </h4>
              <p className="text-red-600 dark:text-red-400 text-sm mt-2">
                Естественные методы контрацепции имеют значительно более низкую эффективность по сравнению с современными методами (гормональная контрацепция, ВМС, барьерные методы). Они не защищают от инфекций, передающихся половым путем (ИППП). Для повышения надежности рекомендуется комбинирование естественных методов с барьерными (презервативы).
              </p>
            </div>
          </>
        )}

        <h3>Физиологические особенности в российской популяции</h3>
        <p>
          Согласно исследованиям, проведенным в России, средняя длительность менструального цикла у российских женщин составляет 28-29 дней, что соответствует общемировым данным. Однако отмечены некоторые региональные особенности:
        </p>
        <ul>
          <li>В северных регионах России наблюдается тенденция к более длинным циклам в зимний период из-за недостатка солнечного света и витамина D</li>
          <li>В крупных городах с высоким уровнем стресса чаще наблюдаются нарушения регулярности цикла</li>
          <li>Средний возраст наступления менопаузы в России составляет 49-50 лет</li>
        </ul>
        <p>
          Важно учитывать, что на регулярность цикла влияют климатические условия, питание, экологическая обстановка и социальные факторы. При планировании беременности или использовании естественных методов контрацепции рекомендуется вести календарь менструального цикла в течение как минимум 6 месяцев для выявления индивидуальных особенностей.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор овуляции поможет вам определить фертильные дни и оптимальное время для зачатия или, наоборот, дни, когда следует использовать дополнительную защиту. Для получения более точных результатов рекомендуется комбинировать расчеты с наблюдением за симптомами овуляции.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;